const RECAPTCHA_ACTION = 'partner_submit';
const RECAPTCHA_SCRIPT_ID = 'partner-recaptcha-script';
const EMAIL_REGEX = /^[\w.+-]+@([\w-]+\.)+[\w-]{2,}$/;
const URL_REGEX = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}([/?#].*)?$/i;

function loadRecaptcha(siteKey)
{
    if (!siteKey || document.getElementById(RECAPTCHA_SCRIPT_ID)) {
        return;
    }

    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
}

function getRecaptchaToken(siteKey)
{
    return new Promise((resolve) => {
        if (!siteKey || typeof window.grecaptcha === 'undefined') {
            resolve('');
            return;
        }

        window.grecaptcha.ready(() => {
            window.grecaptcha
                .execute(siteKey, {action: RECAPTCHA_ACTION})
                .then(resolve)
                .catch(() => resolve(''));
        });
    });
}

function setFieldError(field, message)
{
    const input = field.querySelector('.partner-form__input');
    const error = field.querySelector('.partner-form__error');

    if (input) {
        if (message) {
            input.classList.add('is-invalid');
            input.setAttribute('aria-invalid', 'true');
        } else {
            input.classList.remove('is-invalid');
            input.removeAttribute('aria-invalid');
        }
    }

    if (error) {
        error.textContent = message;
    }
}

function clearAllErrors(form)
{
    form.querySelectorAll('.partner-form__field').forEach((field) => setFieldError(field, ''));
}

function validateField(input)
{
    const value = (input.value || '').trim();
    const name = input.name;
    const required = input.hasAttribute('required');

    if (required && value === '') {
        return 'This field is required.';
    }

    if (value === '') {
        return '';
    }

    if (input.type === 'email' && !EMAIL_REGEX.test(value)) {
        return 'Please enter a valid email address.';
    }

    if (name === 'companyUrl' && !URL_REGEX.test(value)) {
        return 'Please enter a valid URL.';
    }

    if (name === 'phoneNumber') {
        const digits = value.replace(/\D+/g, '');

        if (digits.length !== 10) {
            return 'Please enter a valid 10-digit phone number.';
        }
    }

    return '';
}

function validateForm(form)
{
    let isValid = true;
    const inputs = form.querySelectorAll('.partner-form__input');

    inputs.forEach((input) => {
        const message = validateField(input);
        const field = input.closest('.partner-form__field');

        if (field) {
            setFieldError(field, message);
        }

        if (message) {
            isValid = false;
        }
    });

    return isValid;
}

function setStatus(form, message, state)
{
    const status = form.querySelector('[data-partner-form-status]');

    if (!status) {
        return;
    }

    status.textContent = message || '';
    status.classList.remove('is-success', 'is-error');

    if (state === 'success') {
        status.classList.add('is-success');
    } else if (state === 'error') {
        status.classList.add('is-error');
    }
}

function setSubmitting(form, isSubmitting)
{
    const button = form.querySelector('[data-partner-form-submit]');
    const label = form.querySelector('.partner-form__submit-label');

    form.classList.toggle('is-submitting', isSubmitting);

    if (button) {
        button.disabled = isSubmitting;
        button.classList.toggle('is-loading', isSubmitting);

        if (label) {
            if (isSubmitting) {
                if (!button.dataset.originalLabel) {
                    button.dataset.originalLabel = label.textContent;
                }
                label.textContent = 'Sending...';
            } else if (button.dataset.originalLabel) {
                label.textContent = button.dataset.originalLabel;
                delete button.dataset.originalLabel;
            }
        }
    }
}

async function submitForm(form)
{
    const action = form.dataset.action;
    const recaptchaKey = form.dataset.recaptchaKey || '';
    const successMessage = form.dataset.successMessage || 'Thank you for reaching out.';
    const errorMessage = form.dataset.errorMessage || 'Something went wrong. Please try again.';

    if (!action) {
        setStatus(form, errorMessage, 'error');
        return;
    }

    setSubmitting(form, true);
    setStatus(form, '');

    try {
        const token = await getRecaptchaToken(recaptchaKey);
        const params = new URLSearchParams(new FormData(form));

        if (token) {
            params.set('g-recaptcha-response', token);
        }

        const response = await fetch(action, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            body: params.toString(),
        });

        let data = null;

        try {
            data = await response.json();
        } catch (parseError) {
            data = null;
        }

        if (response.ok && data && data.success) {
            const message = (data.data && data.data.message) || successMessage;
            form.classList.add('is-success');
            setStatus(form, message, 'success');
            form.reset();
        } else {
            const message = (data && data.data && data.data.message) || errorMessage;
            setStatus(form, message, 'error');
        }
    } catch (error) {
        setStatus(form, errorMessage, 'error');
    } finally {
        setSubmitting(form, false);
    }
}

function bindForm(form)
{
    if (form.dataset.partnerFormBound === 'true') {
        return;
    }

    form.dataset.partnerFormBound = 'true';

    const recaptchaKey = form.dataset.recaptchaKey || '';

    if (recaptchaKey) {
        loadRecaptcha(recaptchaKey);
    }

    form.querySelectorAll('.partner-form__input').forEach((input) => {
        input.addEventListener('blur', () => {
            const field = input.closest('.partner-form__field');
            const message = validateField(input);

            if (field) {
                setFieldError(field, message);
            }
        });

        input.addEventListener('input', () => {
            if (!input.classList.contains('is-invalid')) {
                return;
            }

            const field = input.closest('.partner-form__field');
            const message = validateField(input);

            if (field) {
                setFieldError(field, message);
            }
        });
    });

    form.addEventListener('submit', (event) => {
        event.preventDefault();

        clearAllErrors(form);

        if (!validateForm(form)) {
            setStatus(form, 'Please correct the errors above and try again.', 'error');
            return;
        }

        submitForm(form);
    });
}

export default function init()
{
    const forms = document.querySelectorAll('[data-partner-form]');

    forms.forEach(bindForm);
}
